import { Button } from '@calizahq/react-components-v2'
import { Form, Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'

import { DateInputControlled, TimeInputControlled } from '@/components'
import { Task } from '@/graphql/mutations/task.mutations'
import { TaskManager } from '@/graphql/mutations/taskManager.mutations'
import { useGraphqlMutation } from '@/hooks/useGraphql'
import { PlannerItemResponse, ScheduledPlannerItem } from '@/types/Planner'
import { convertLocalTimeToUtc, convertUtcToLocalTime, formatDate } from '@/util'

type Props = {
  item: ScheduledPlannerItem | PlannerItemResponse | null
  onCancel: () => void
  onSuccess: () => void
}

const RescheduleForm = ({ item, onCancel, onSuccess }: Props) => {
  const { mutate: mutateTaskManager, isPending: isTaskManagerLoading } = useGraphqlMutation(
    TaskManager.updateTaskManager(),
    {
      onSuccess: () => {
        onSuccess()
      },
    },
  )
  const { mutate: mutateTask, isPending: isTaskLoading } = useGraphqlMutation(Task.updateTasks(), {
    onSuccess: () => {
      onSuccess()
    },
  })

  const onSubmit = (values) => {
    const dueDate = formatDate(values?.dueDate, 'yyyy-MM-dd')
    const startTime = formatDate(values?.startTime, 'kk:mm')
    const startTimeWithDueDate = convertLocalTimeToUtc(`${dueDate}T${startTime}`)
    const endTime = formatDate(values?.endTime, 'kk:mm')
    const endTimeWithDueDate = convertLocalTimeToUtc(`${dueDate}T${endTime}`)

    switch (item?.recordType) {
      case 'task manager':
        mutateTaskManager({
          id: item?.recordId,
          taskManager: {
            startTime: startTimeWithDueDate,
            endTime: endTimeWithDueDate,
            dueDate: dueDate,
          },
        })
        return
      case 'task':
        mutateTask({
          taskIds: [item?.recordId],
          task: {
            startTime: startTimeWithDueDate,
            endTime: endTimeWithDueDate,
            dueDate: dueDate,
          },
        })
        return
      default:
        return
    }
  }

  return (
    <Formik
      initialValues={{
        dueDate: item?.dueDate ? convertUtcToLocalTime(item?.dueDate) : '',
        startTime: item?.startTime ? convertUtcToLocalTime(item?.startTime) : '',
        endTime: item?.endTime ? convertUtcToLocalTime(item?.endTime) : '',
      }}
      onSubmit={onSubmit}
      validationSchema={formValidation}
    >
      {() => {
        return (
          <>
            <Form className="flex w-full flex-col px-6" id="planner-detail-form">
              <DateInputControlled className="mb-4 w-full" label="Due Date" name="dueDate" />
              <TimeInputControlled className="mb-4 w-full" label="Start Time" name="startTime" />
              <TimeInputControlled className="mb-4 w-full" label="End Time" name="endTime" />
            </Form>
            <div className="flex items-center border-t border-grey-100 px-6 pt-4">
              <Button className="mr-1 flex-1 bg-grey-100 " onClick={onCancel} variant="secondary">
                Cancel
              </Button>
              <Button
                className="ml-1 flex-1 bg-text-semantic-black px-6 hover:bg-text-semantic-black"
                disabled={isTaskLoading || isTaskManagerLoading}
                form="planner-detail-form"
                isLoading={isTaskLoading || isTaskManagerLoading}
                type="submit"
              >
                Save
              </Button>
            </div>
          </>
        )
      }}
    </Formik>
  )
}

const formValidation = Yup.object({
  startTime: Yup.string().required('Start time required'),
  endTime: Yup.string().required('End time required'),
  dueDate: Yup.string().required('Due date required'),
})

export default RescheduleForm
